import { z } from 'zod'
import './registry'

export const NoteLanguageSchema = z.enum(['NL', 'EN']).openapi('NoteLanguage')

export const GenerateNoteRequestSchema = z
  .object({
    artist: z.string().min(1).max(200),
    title: z.string().min(1).max(200),
    // Bound every field that flows into the Anthropic prompt. These are
    // attacker- or Discogs-supplied and feed a metered LLM call, so the schema
    // (the validation boundary) must cap both length and array size — not rely
    // on the handler's defensive slice. Ranges mirror RecordSchema where the
    // same field exists. releaseNotes is generous (the handler further slices
    // it to ~1500 before prompting); the cap is a cost/injection guard.
    year: z.number().int().min(1900).max(2100).nullable().optional(),
    country: z.string().max(80).nullable().optional(),
    genres: z.array(z.string().max(80)).max(20).optional(),
    styles: z.array(z.string().max(80)).max(20).optional(),
    labelName: z.string().max(120).nullable().optional(),
    catno: z.string().max(120).nullable().optional(),
    releaseNotes: z.string().max(5000).nullable().optional(),
    communityRating: z
      .object({
        average: z.number().min(0).max(5),
        count: z.number().int().min(0),
      })
      .optional(),
    language: NoteLanguageSchema.optional(),
  })
  .openapi('GenerateNoteRequest')

export const GenerateNoteResponseSchema = z
  .object({
    note: z.string(),
    fallback: z.boolean(),
    /**
     * Why the fallback text was served (null on the research path).
     * 'overloaded' — Anthropic was slow, rate-limited or down: retrying in a
     * few minutes genuinely helps, and the UI says so. 'other' — the agent
     * finished without a usable note. Defaulted so a response from a
     * not-yet-redeployed backend still parses client-side.
     */
    fallbackReason: z.enum(['overloaded', 'other']).nullable().default(null),
  })
  .openapi('GenerateNoteResponse')

export const TranslateNoteRequestSchema = z
  .object({
    text: z.string().min(1).max(1000),
    language: NoteLanguageSchema,
  })
  .openapi('TranslateNoteRequest')

export const TranslateNoteResponseSchema = z
  .object({
    note: z.string(),
  })
  .openapi('TranslateNoteResponse')

export type NoteLanguage = z.infer<typeof NoteLanguageSchema>
export type GenerateNoteRequest = z.infer<typeof GenerateNoteRequestSchema>
export type GenerateNoteResponse = z.infer<typeof GenerateNoteResponseSchema>
export type TranslateNoteRequest = z.infer<typeof TranslateNoteRequestSchema>
export type TranslateNoteResponse = z.infer<typeof TranslateNoteResponseSchema>
